































import Component, { mixins } from "vue-class-component";
import { Prop, Provide, ProvideReactive, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import {
  ContentFile,
  InputContentFileCreate,
  InputContentFileUpdate,
} from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { MetadataField } from "@scrile/api-provider/dist/api/FileProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { showDefaultUploadError } from "@/lib/FileUploader";
import logger from "@/modules/logger";
import { AttachmentFile, PostFileData } from "@/types";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";
import { AddingFilesOptions, DraggableEmitOptions } from "../../types";
import ChoosePostTypeController from "../ChoosePostTypeController";
import CreatePostView from "../CreatePostView";
import GoingLiveConfirm from "../GoingLiveConfirm";

interface FileUploadData {
  id: string;
  file: File;
  uploadProgress: number;
}

function isFileUploadData(data: any): data is FileUploadData {
  return typeof data.file !== "undefined";
}

// inject: [] - temporary bug fix for ProvideReactive: https://github.com/kaorun343/vue-property-decorator/issues/277
@Component({
  name: "CreatePostController",
  components: { CreatePostView, ModalView, ChoosePostTypeController, GoingLiveConfirm },
  inject: [],
})
export default class CreatePostController extends mixins(WithAuthUser) {
  @Prop({ type: String, required: true })
  public readonly postId!: string;

  @Prop({ type: String, required: true })
  public readonly type!: ContentFeedTypeEnum;

  @Prop({ type: Boolean, required: true })
  public readonly show!: boolean;

  @ProvideReactive("contentFeedUserId")
  @Prop({ type: String, required: true })
  public readonly userId!: string;

  public loading: boolean = false;

  public saving: Promise<any> | null = null;

  public postData: ContentSet | null = null;

  public postFiles: (ContentFile | FileUploadData)[] = [];

  public updateCb: (() => Promise<void>)[] = [];

  public showLiveConfirm = false;

  public postType: ContentFeedTypeEnum | null = null;

  public get showChooseType(): boolean {
    return !this.loading && !this.postData && !this.showLiveConfirm;
  }

  public get isPostEmpty(): boolean {
    if (!this.postData) return true;
    return !(this.postData?.description || this.postFiles.length > 0);
  }

  @ProvideReactive("fileList")
  public get postFileDataList(): PostFileData[] {
    return this.postFiles.map(i => {
      if (isFileUploadData(i)) {
        return {
          url: "",
          fileName: i.file.name,
          duration: null,
          uploading: true,
          uploadProgress: i.uploadProgress,
          mediaType: getFileMimeType(i.file),
          size: i.file.size,
          public: false,
          id: i.id,
          processingStatus: "NEW",
          metadata: {},
        };
      } else {
        return {
          id: i.id,
          mediaType: i.mediaType,
          fileName: i.title,
          size: i.size,
          public: i.publicAccess,
          uploading: false,
          duration: i.duration,
          url: i.urlPart,
          uploadProgress: 100,
          processingStatus: i.processingStatus,
          metadata: "metadata" in i ? i["metadata"] : {},
        };
      }
    });
  }

  @Watch("postId", { immediate: true })
  public async watchPostId(postId: string) {
    if (!this.postData && postId) {
      try {
        this.loading = true;
        await Promise.all([this.requestAndSetPostData(postId), this.requestAndSetPostFiles(postId)]);
      } finally {
        this.loading = false;
      }
    }
  }

  public requestAndSetPostData(postId: string) {
    return providers.ContentSetProvider.search({
      data: {
        userId: this.userId,
        ids: [postId],
      },
    }).then(resp => {
      this.postData = resp.result.find(i => i.id === postId) || null;
      this.$emit("updatePostData", this.postData);
    });
  }

  public requestAndSetPostFiles(postId: string) {
    return providers.ContentFilesProvider.search({
      data: {
        setId: [postId],
        size: 9999,
        sort: "SORT,ASC",
      },
    }).then(resp => {
      this.postFiles = resp.result;
    });
  }

  public async createPost(type: ContentFeedTypeEnum) {
    try {
      this.loading = true;
      this.postData = await providers.ContentSetProvider.create({
        data: {
          type,
          userId: this.userId,
        },
      });
      this.$emit("updatePostData", this.postData);
    } finally {
      this.loading = false;
    }
  }

  public async onCreatePost(type: ContentFeedTypeEnum, cb?: () => void) {
    try {
      await this.createPost(type);
    } finally {
      cb && cb();
    }
  }

  public async onSetPostType(type: ContentFeedTypeEnum) {
    this.postType = type;
  }

  public onSetPostData(data: ContentSet) {
    this.postData = data;
    this.requestAndSetPostFiles(data.id);
  }

  public onSetUpdateCb(cb: () => Promise<void>) {
    this.updateCb.push(cb);
  }

  public async onUpdate(
    {
      data,
      closeOnSave,
      beforeUpdate,
    }: { data: Omit<InputContentSetUpdate, "id">; closeOnSave?: boolean; beforeUpdate?: () => Promise<void[]> },
    cb?: Function
  ) {
    const post = await this.getPostData();
    let deferred: Function = () => {};
    try {
      this.saving = new Promise<any>(r => {
        deferred = r;
      });
      const promises: Promise<any>[] = [this.setPostFilePublicAccess(post.id)];
      if (data.published) {
        promises.push(this.waitFileUpload(), this.setPostFileSorting(post.id));
      }
      await Promise.all(promises);
      beforeUpdate && (await beforeUpdate());
      this.postData = await providers.ContentSetProvider.update({
        data: {
          ...data,
          id: post.id,
        },
      });
      this.$emit("updatePostData", this.postData);
      if (closeOnSave) {
        this.reset();
      }
    } finally {
      cb && cb();
      deferred();
      this.saving = null;
    }
  }

  public async onFileRemove(fileId: string) {
    const file = this.postFiles.find(f => f.id === fileId);
    if (!file) return;

    this.postFiles = this.postFiles.filter(i => i.id !== fileId);
    if (isFileUploadData(file)) {
      FileUploader.removeFromQueue(fileId);
    } else {
      const post = await this.getPostData();
      await providers.ContentFilesProvider.delete({ setId: post.id, id: fileId });
    }
  }

  public async getPostData() {
    return this.postData || (await new Promise<ContentSet>(r => this.$once("updatePostData", (p: any) => r(p))));
  }

  @Provide("updatePostData")
  public updatePostData(postData: ContentSet) {
    this.postData = postData;
  }

  @Provide("postAddFiles")
  public async onAddFiles(
    files: FileList,
    {
      unshift = false,
      publicAccess = false,
      metadata = {},
    }: { unshift?: boolean; publicAccess?: boolean; metadata?: Record<MetadataField | string, string> } = {}
  ) {
    const post = await this.getPostData();
    const uploadUrls = await providers.ContentFilesProvider.getUploadUrls({
      setId: post.id,
      count: files.length,
    });
    const uploadData: UploadFileData[] = [];
    Array.from(files).forEach((file, index) => {
      const uuid = uploadUrls[index].uuid;
      const id = "file__" + uuid;
      this.postFiles[unshift ? "unshift" : "push"]({ id, file, uploadProgress: 0 });
      const data: UploadFileData = {
        urlPart: uploadUrls[index].urlPart,
        fileType: getFileMimeType(file),
        file,
        id,
        onSuccess: () => this.onFileUploaded({ id, post, file, uuid, publicAccess, metadata }),
        onUploadProgress: p => {
          this.postFiles.find((i, index) => {
            if (i.id === id && isFileUploadData(i)) {
              this.$set(this.postFiles, index, {
                ...i,
                uploadProgress: Math.floor(((p?.loaded ?? 0) / (p?.total ?? 0)) * 100),
              });
              return true;
            }
            return false;
          });
        },
        onError: e => {
          if (!this.postFiles.some(i => i.id === id)) {
            return;
          }
          logger.debug("Upload error", { dataId: data.id, dataPartUrl: data.urlPart, error: e });
          console.error(e);
          showDefaultUploadError();
          this.onFileRemove(data.id);
        },
      };
      uploadData.push(data);
    });
    FileUploader.addToQueue(uploadData);
  }

  public async onFileUploaded({
    id,
    uuid,
    file,
    post,
    publicAccess,
    metadata,
  }: {
    id: string;
    uuid: string;
    file: File;
    post: ContentSet;
    publicAccess: boolean;
    metadata: Record<MetadataField | string, string>;
  }) {
    const inputFileData: InputContentFileCreate = {
      title: file.name,
      name: file.name,
      uuid,
      publicAccess,
      metadata,
    };
    const contentFile = await providers.ContentFilesProvider.create({ setId: post.id, data: inputFileData });
    this.postFiles = this.postFiles.map(i => {
      return i.id === id ? contentFile : i;
    });
    this.setPostFileSorting(post.id);
  }

  public async setPostFileSorting(postId: string) {
    if (!this.postFiles.find(i => isFileUploadData(i))) {
      for (const [index, fileData] of this.postFiles.entries()) {
        await providers.ContentFilesProvider.update({
          setId: postId,
          data: {
            id: fileData.id,
            sort: index,
          },
        });
      }
    }
  }

  public async setPostFilePublicAccess(postId: string) {
    const filesUpdatePromises = this.postFiles.map(
      file =>
        (!isFileUploadData(file) &&
          providers.ContentFilesProvider.update({
            setId: postId,
            data: {
              id: file.id,
              publicAccess: file.publicAccess,
              metadata: file.metadata,
            },
          })) ||
        undefined
    );
    await Promise.all<void | ContentFile>(filesUpdatePromises);
  }

  public onFileChange(data: InputContentFileUpdate) {
    this.postFiles = this.postFiles.map(i => {
      if (i.id === data.id && !isFileUploadData(i)) {
        return { ...i, ...data };
      }
      return i;
    });
  }

  public async onDraggableUpdate({ newDraggableIndex, oldDraggableIndex }: DraggableEmitOptions): Promise<void> {
    this.postFiles.splice(newDraggableIndex, 0, this.postFiles.splice(oldDraggableIndex, 1)[0]);
    try {
      await this.setPostFileSorting(this.postData?.id ?? "");
    } catch (e) {
      this.postFiles.splice(oldDraggableIndex, 0, this.postFiles.splice(newDraggableIndex, 1)[0]);
    }
  }

  public async saveDraft() {
    const update = () => Promise.all(this.updateCb.map(i => i()));
    return this.onUpdate({ data: { published: false }, closeOnSave: false, beforeUpdate: update });
  }

  public async deleteDraft() {
    await this.saving;
    if (!this.postData) return;
    await providers.ContentSetProvider.delete({ id: this.postData.id });
  }

  public async onCloseModal() {
    await this.saving;
    if (this.postData && !this.postData.published) {
      this.isPostEmpty ? await this.deleteDraft() : await this.saveDraft();
      this.$emit("close");
      return;
    }
    this.reset();
  }

  public async waitFileUpload(): Promise<void> {
    if (this.postFileDataList.find(i => i.uploading)) {
      await new Promise(r => setTimeout(r, 500));
      return this.waitFileUpload();
    }
  }

  public async onAddFilesFromVault(files: AttachmentFile[], options?: AddingFilesOptions) {
    const post = await this.getPostData();
    for (const file of files) {
      const resultFile = await providers.ContentFilesProvider.create({
        setId: post.id,
        data: {
          name: file.metadata?.filename ?? file.uid,
          uuid: file.uid,
          publicAccess: options?.publicAccess ?? false,
          title: file.metadata?.filename ?? file.uid,
          ...(options?.metadata?.stereoFormat ? { metadata: { stereoFormat: options.metadata.stereoFormat } } : {}),
        },
      });
      this.postFiles.push(resultFile);
    }
  }

  public reset() {
    this.$emit("close");
    if (this.postData) {
      emitter.emit(EVENTS.CONTENT_FEED_UPDATED, { userId: this.postData.userId, feedId: this.postData.id });
    }
    this.postData = null;
    this.postFiles.forEach(i => {
      if (isFileUploadData(i)) {
        this.onFileRemove(i.id);
      }
    });
    this.postFiles.length > 0 && (this.postFiles = []);
  }

  public created() {
    if (this.type.length > 0) this.onSetPostType(this.type);
    const unWatch = this.$watch(
      () => this.$route,
      (to: Route, from: Route | null) => {
        if (!from || to.name === from.name) return;
        this.onCloseModal();
      }
    );
    const onPriceChanged = ({
      recurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      const publicAccess = !recurringLevel && !oneTimePackage;
      if (
        (publicAccess && this.postFileDataList.some(f => !f.public)) ||
        (!publicAccess && this.postFileDataList.every(f => f.public))
      ) {
        this.postFiles = this.postFiles.map(file => {
          if (!isFileUploadData(file)) {
            file.publicAccess = publicAccess;
          }
          return file;
        });
      }
    };
    emitter.on<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
    this.$once("hook:beforeDestroy()", () => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
      unWatch();
    });
  }
}
